// ============================================================================
//  dAdmin  ›  dAttendance  ›  Activity History   (spec page 4)
//
//  Read-only. The four employee actions the spec asks for — Save, Submit, Edit
//  request, Download — plus the admin-side decisions and reminder sends, since
//  they belong on the same timeline when you are reconstructing what happened
//  to a month.
// ============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { FaFileExcel } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { exportTicketsExcel } from "./utils/ticketExport";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dAttendance_History.css";

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

const ACTION_TONE = {
    Saved: "grey", Submitted: "green", "Edit request": "orange", Downloaded: "grey",
    Approved: "green", Rejected: "red", "Edit accepted": "orange",
    "Reminder sent": "navy", "Pattern changed": "amber",
};

const PAGE_SIZE = 100;

export default function DAttendanceHistory({ navSize }) {
    const [filters, setFilters] = useState({ employees: [], actions: [] });
    const [empId, setEmpId] = useState("");
    const [action, setAction] = useState("");
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dattendance/history/filters");
                const json = await res.json();
                if (json.success) setFilters(json);
            } catch { /* the list load reports errors */ }
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const p = new URLSearchParams({ limit: PAGE_SIZE, offset });
            if (empId) p.set("emp_id", empId);
            if (action) p.set("action", action);
            if (year) p.set("year", year);
            if (month) p.set("month", month);
            const res = await apiFetch(`/api/dattendance/history/list?${p.toString()}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Failed to load");
            setRows(json.activities);
            setTotal(json.total);
        } catch (err) { setRows([]); setError(err.message); }
        finally { setLoading(false); }
    }, [empId, action, year, month, offset]);

    useEffect(() => { load(); }, [load]);

    // Any filter change resets paging - otherwise you land on page 3 of a
    // one-page result and see nothing.
    const setFilter = (setter) => (value) => { setter(value); setOffset(0); };

    const years = [];
    for (let y = new Date().getFullYear(); y >= 2018; y--) years.push(y);

    // Excel export of the whole filtered set (not just the current page).
    const exportExcel = async () => {
        const p = new URLSearchParams({ limit: 1000, offset: 0 });
        if (empId) p.set("emp_id", empId);
        if (action) p.set("action", action);
        if (year) p.set("year", year);
        if (month) p.set("month", month);
        let list = rows;
        try {
            const res = await apiFetch(`/api/dattendance/history/list?${p.toString()}`);
            const json = await res.json();
            if (res.ok) list = json.activities || [];
        } catch { /* fall back to the current page */ }
        const columns = [
            { key: "when",   label: "When",     width: 20 },
            { key: "emp",    label: "Employee", width: 24 },
            { key: "emp_id", label: "Emp ID",   width: 14 },
            { key: "act",    label: "Action",   width: 16 },
            { key: "period", label: "Period",   width: 16 },
            { key: "detail", label: "Detail",   width: 40, wrap: true },
            { key: "by",     label: "By",       width: 22 },
            { key: "source", label: "Source",   width: 16 },
        ];
        const data = list.map((r) => ({
            when: String(r.created_time).replace("T", " ").slice(0, 16),
            emp: r.emp_name, emp_id: r.emp_id, act: r.action,
            period: r.month ? `${MONTHS[r.month - 1]} ${r.year}` : "-",
            detail: r.detail || "-", by: r.by_admin ? r.actor_name : "Self",
            source: r.ip_address || "-",
        }));
        exportTicketsExcel({
            moduleName: "dAttendance", sheetTitle: "Activity History",
            columns, rows: data, filterLabel: "",
            filename: `attendance_activity_${new Date().toISOString().slice(0, 10)}.xlsx`,
        });
    };

    return (
        <div className="dspr-container">
          <div className={`dspr-dashboard ${navSize || ""}`}>
            <div className="dspr-header-wrapper">
              <h2>
                <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                Activity History
              </h2>
              <p className="dspr-subtitle">
                Every save, submit, edit request and download from the employee app, newest
                first — alongside the admin decisions and reminder sends for the same month.
              </p>
            </div>
            <div className="dspr-dashboard-content datt-page">
              <div className="datt-toolbar">
                <div className="datt-hist-filters">
                    <select value={empId} onChange={(e) => setFilter(setEmpId)(e.target.value)}>
                        <option value="">All employees</option>
                        {filters.employees.map((e) => (
                            <option key={e.emp_id} value={e.emp_id}>{e.emp_name} ({e.emp_id})</option>
                        ))}
                    </select>
                    <select value={action} onChange={(e) => setFilter(setAction)(e.target.value)}>
                        <option value="">All actions</option>
                        {filters.actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={year} onChange={(e) => setFilter(setYear)(e.target.value)}>
                        <option value="">Any year</option>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={month} onChange={(e) => setFilter(setMonth)(e.target.value)}>
                        <option value="">Any month</option>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                    <button type="button" className="datt-btn is-export" onClick={exportExcel}
                        disabled={total === 0} title="Export the filtered activity to Excel">
                        <FaFileExcel style={{ marginRight: 6 }} /> Export Excel
                    </button>
                </div>
            </div>

            {error && <div className="datt-notice is-red">{error}</div>}

            <div className="datt-card">
                <div className="datt-card-head">
                    <span className="datt-card-title">
                        {total} {total === 1 ? "entry" : "entries"}
                    </span>
                    {total > PAGE_SIZE && (
                        <span className="datt-pager">
                            <button type="button" className="datt-btn is-ghost is-sm" disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
                            <span className="datt-muted datt-small">
                                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)}
                            </span>
                            <button type="button" className="datt-btn is-ghost is-sm"
                                disabled={offset + PAGE_SIZE >= total}
                                onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="datt-loading">Loading…</div>
                ) : (
                    <div className="datt-table-wrap">
                        <table className="datt-table">
                            <thead>
                                <tr>
                                    <th>When</th><th>Employee</th><th>Action</th>
                                    <th>Period</th><th>Detail</th><th>By</th><th>Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={7} className="datt-empty">No activity matches these filters.</td></tr>
                                )}
                                {rows.map((r) => (
                                    <tr key={r.activity_id}>
                                        <td className="datt-muted datt-nowrap">
                                            {String(r.created_time).replace("T", " ").slice(0, 16)}
                                        </td>
                                        <td>
                                            <div className="datt-strong">{r.emp_name}</div>
                                            <div className="datt-mono datt-muted">{r.emp_id}</div>
                                        </td>
                                        <td>
                                            <span className={`datt-pill is-${ACTION_TONE[r.action] || "grey"}`}
                                                  style={{ marginLeft: 0 }}>{r.action}</span>
                                        </td>
                                        <td>{r.month ? `${MONTHS[r.month - 1]} ${r.year}` : "-"}</td>
                                        <td className="datt-muted datt-detail">{r.detail || "-"}</td>
                                        <td className="datt-muted datt-small">
                                            {r.by_admin ? r.actor_name : "Self"}
                                        </td>
                                        <td className="datt-muted datt-small">{r.ip_address || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            </div>
          </div>
        </div>
    );
}
